// Shared patient data enrichment logic for Hospital and Hospital_Doctor dashboards
import { calculateOverallMentalHealthScore } from './mentalStateCalculations';

export function enrichPatientData(patient, sessionHistory) {
  if (!patient || !sessionHistory || sessionHistory.length === 0) {
    return {
      ...patient,
      emotionVector: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 },
      weeklyReport: [],
      monthlyReport: [],
      riskComponents: {
        emotional_anomaly: 0,
        behavioral_deviation: 0,
        burnout: 0,
        intent_score: 0
      },
      overallMark: 0,
      cumulativeRisk: 0,
      hasSessionData: false
    };
  }

  const sessions = sessionHistory;
  const latestSession = sessions[0];

  const emotionVector = latestSession.emotion ? {
    joy: latestSession.emotion.joy || 0,
    stress: latestSession.emotion.stress || 0,
    sadness: latestSession.emotion.sadness || 0,
    anger: latestSession.emotion.anger || 0,
    hurt: latestSession.emotion.hurt || 0
  } : { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 };

  const burnout = latestSession.emotion
    ? (latestSession.emotion.burnout_score ?? Math.round(((latestSession.emotion.stress || 0) + (latestSession.emotion.sadness || 0) + (latestSession.emotion.anger || 0)) / 3))
    : 0;
  const overallMark = Math.max(0, 100 - burnout);

  const toNum = (v) => (Number.isFinite(v) ? v : 0);
  const riskComponents = latestSession.emotion ? {
    emotional_anomaly: Math.max(0, Math.min(100, Math.round((toNum(latestSession.emotion.stress) + toNum(latestSession.emotion.sadness) + toNum(latestSession.emotion.anger)) / 3))),
    behavioral_deviation: Math.max(0, Math.min(100, Math.round((toNum(latestSession.emotion.anger) + toNum(latestSession.emotion.hurt)) / 2))),
    burnout: Math.max(0, Math.min(100, Math.round(burnout))),
    intent_score: Math.max(0, Math.min(100, Math.round((toNum(latestSession.emotion.sadness) + toNum(latestSession.emotion.hurt)) / 2)))
  } : {
    emotional_anomaly: 0,
    behavioral_deviation: 0,
    burnout: 0,
    intent_score: 0
  };

  const cumulativeRisk = 0.4 * riskComponents.emotional_anomaly +
    0.3 * riskComponents.behavioral_deviation +
    0.2 * riskComponents.burnout +
    0.1 * riskComponents.intent_score;

  const recentSessions = sessions.slice(0, 7).reverse();

  const weeklyReport = recentSessions.map((session) => {
    if (session.emotion) {
      return {
        score: calculateOverallMentalHealthScore(session.emotion),
        emotions: {
          joy: session.emotion.joy || 0,
          stress: session.emotion.stress || 0,
          sadness: session.emotion.sadness || 0,
          anger: session.emotion.anger || 0,
          hurt: session.emotion.hurt || 0,
          burnout_score: session.emotion.burnout_score || 0
        },
        sessionDate: session.session_date,
        sessionId: session.session_id
      };
    }
    return {
      score: 0,
      emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
      sessionDate: null,
      sessionId: null
    };
  });

  while (weeklyReport.length < 7) {
    weeklyReport.unshift({
      score: 0,
      emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
      sessionDate: null,
      sessionId: null
    });
  }

  const monthlyData = {};
  sessions.forEach(session => {
    if (session.session_date && session.emotion) {
      const month = session.session_date.substring(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = [];
      }
      monthlyData[month].push({
        score: calculateOverallMentalHealthScore(session.emotion),
        emotions: {
          joy: session.emotion.joy || 0,
          stress: session.emotion.stress || 0,
          sadness: session.emotion.sadness || 0,
          anger: session.emotion.anger || 0,
          hurt: session.emotion.hurt || 0,
          burnout_score: session.emotion.burnout_score || 0
        }
      });
    }
  });

  const monthKeys = Object.keys(monthlyData).sort().slice(-4);
  const monthlyReport = monthKeys.map(month => {
    const monthSessions = monthlyData[month];
    const avgScore = Math.round(monthSessions.reduce((sum, s) => sum + s.score, 0) / monthSessions.length);
    const avgEmotions = monthSessions.reduce((acc, s) => {
      acc.joy += s.emotions.joy;
      acc.stress += s.emotions.stress;
      acc.sadness += s.emotions.sadness;
      acc.anger += s.emotions.anger;
      acc.hurt += s.emotions.hurt;
      return acc;
    }, { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 });
    Object.keys(avgEmotions).forEach(key => {
      avgEmotions[key] = Math.round(avgEmotions[key] / monthSessions.length);
    });
    return {
      score: avgScore,
      emotions: avgEmotions,
      month: month,
      sessionCount: monthSessions.length
    };
  });

  while (monthlyReport.length < 4) {
    monthlyReport.unshift({
      score: 0,
      emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
      month: null,
      sessionCount: 0
    });
  }

  return {
    ...patient,
    emotionVector,
    weeklyReport,
    monthlyReport,
    riskComponents,
    overallMark,
    cumulativeRisk,
    hasSessionData: true
  };
}
